import { getCookie, setCookie } from '@lib/cookies';

export const SESSION_TRANSFER_TOKEN_PARAM = 'session_transfer_token';

export const SSO_HINT_COOKIE = 'naf.sso';
export const SSO_HINT_DOMAIN = 'naf.no';

const SSO_HINT_VALUE = '1';
const SSO_HINT_DAYS = 1;

export const SESSION_GONE_ERRORS = ['login_required', 'consent_required'];

const RELATIVE_URL_BASE = 'https://relative.invalid';

const canUseCookies = () => typeof window !== 'undefined' && typeof document !== 'undefined';

export const isSsoHintHost = (hostname: string): boolean =>
  hostname === SSO_HINT_DOMAIN || hostname.endsWith(`.${SSO_HINT_DOMAIN}`);

const hintDomain = () => (isSsoHintHost(window.location.hostname) ? SSO_HINT_DOMAIN : undefined);

const isSecureContext = () => window.location.protocol === 'https:';

// Auth0 iframe silent auth only sees the shared session on naf.no hosts. Off-domain
// (localhost) it always fails with login_required, which clears the local session.
export const canUseSsoSession = (): boolean => canUseCookies() && isSsoHintHost(window.location.hostname);

export const hasSsoHint = (): boolean => canUseCookies() && getCookie(SSO_HINT_COOKIE) === SSO_HINT_VALUE;

export const setSsoHint = (): void => {
  if (!canUseCookies()) return;
  setCookie(SSO_HINT_COOKIE, SSO_HINT_VALUE, SSO_HINT_DAYS, 'Lax', isSecureContext(), hintDomain());
};

export const clearSsoHint = (): void => {
  if (!canUseCookies()) return;
  setCookie(SSO_HINT_COOKIE, '', -1, 'Lax', isSecureContext(), hintDomain());
};

const isAbsoluteUrl = (urlString: string) => /^https?:\/\//i.test(urlString);

const parseUrl = (urlString: string) => {
  const absolute = isAbsoluteUrl(urlString);
  return { url: new URL(urlString, absolute ? undefined : RELATIVE_URL_BASE), absolute };
};

const serializeUrl = (url: URL, absolute: boolean) =>
  absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;

const withTrailingSlash = (url: string) => (url.endsWith('/') ? url : `${url}/`);

export const isAllowedUrlForSessionTransfer = (urlString: string, allowedBaseUrls: readonly string[]): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(urlString);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;

  const normalized = withTrailingSlash(`${parsed.origin}${parsed.pathname}`);
  return allowedBaseUrls
    .filter((base) => typeof base === 'string' && base.length > 0)
    .map(withTrailingSlash)
    .some((prefix) => normalized.startsWith(prefix));
};

export const appendSessionTransferToken = (
  urlString: string,
  token: string,
  allowedBaseUrls: readonly string[],
): string => {
  try {
    const { url, absolute } = parseUrl(urlString);
    if (absolute && !isAllowedUrlForSessionTransfer(urlString, allowedBaseUrls)) return urlString;

    url.searchParams.set(SESSION_TRANSFER_TOKEN_PARAM, token);
    return serializeUrl(url, absolute);
  } catch {
    return urlString;
  }
};

export const stripSessionTransferToken = (urlString: string): string => {
  try {
    const { url, absolute } = parseUrl(urlString);
    if (!url.searchParams.has(SESSION_TRANSFER_TOKEN_PARAM)) return urlString;

    url.searchParams.delete(SESSION_TRANSFER_TOKEN_PARAM);
    return serializeUrl(url, absolute);
  } catch {
    return urlString;
  }
};
